import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  ArrowLeft,
  Save,
  Calendar,
  Clock,
  User,
  FileText,
  AlertCircle,
} from "lucide-react";
import Select from "react-select";
import medicalDB from "../services/database";

const AppointmentForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;
  const preSelectedPatientId = searchParams.get("patientId");
  const preSelectedDate = searchParams.get("date");

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [suggestedSlots, setSuggestedSlots] = useState([]);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      patientId: preSelectedPatientId || "",
      date: preSelectedDate || "", // Pre-populate date
      startTime: "",
      type: "ROUTINE",
      notes: "",
    },
  });

  const selectedType = watch("type");
  const selectedDate = watch("date");
  const selectedStartTime = watch("startTime");

  useEffect(() => {
    loadPatients();
    if (isEditing) {
      loadAppointment();
    }
  }, [id, isEditing]);

  useEffect(() => {
    // Auto-calculate end time when start time or type changes
    if (selectedStartTime && selectedType) {
      const appointmentTypes = {
        ROUTINE: { name: "Routine Checkup", duration: 30 },
        FOLLOW_UP: { name: "Follow-up", duration: 20 },
        NEW_PATIENT: { name: "New Patient", duration: 45 },
        CONSULTATION: { name: "Consultation", duration: 30 },
        EMERGENCY: { name: "Emergency", duration: 15 },
      };
      const duration = appointmentTypes[selectedType]?.duration || 30;
      const endTime = calculateEndTime(selectedStartTime, duration);
      setValue("endTime", endTime);
    }
  }, [selectedStartTime, selectedType, setValue]);

  // Check for conflicts when date, start time, or end time changes
  useEffect(() => {
    const checkConflicts = async () => {
      if (selectedDate && selectedStartTime && watch("endTime")) {
        try {
          const appointmentData = {
            date: selectedDate,
            startTime: selectedStartTime,
            endTime: watch("endTime"),
          };

          const foundConflicts = await medicalDB.checkAppointmentConflicts(
            appointmentData,
            isEditing ? id : null
          );

          setConflicts(foundConflicts);
          setShowConflictWarning(foundConflicts.length > 0);

          // Get suggested alternative slots if there are conflicts
          if (foundConflicts.length > 0) {
            const appointmentTypes = {
              ROUTINE: 30,
              FOLLOW_UP: 20,
              NEW_PATIENT: 45,
              CONSULTATION: 30,
              EMERGENCY: 15,
            };
            const duration = appointmentTypes[selectedType] || 30;
            const suggestions = await medicalDB.getSuggestedTimeSlots(
              selectedDate,
              duration
            );
            setSuggestedSlots(suggestions);
          } else {
            setSuggestedSlots([]);
          }
        } catch (error) {
          console.error("Error checking conflicts:", error);
        }
      }
    };

    checkConflicts();
  }, [
    selectedDate,
    selectedStartTime,
    watch("endTime"),
    selectedType,
    id,
    isEditing,
  ]);

  // Load available time slots when date changes
  useEffect(() => {
    const loadAvailableSlots = async () => {
      if (selectedDate && selectedType) {
        try {
          const appointmentTypes = {
            ROUTINE: 30,
            FOLLOW_UP: 20,
            NEW_PATIENT: 45,
            CONSULTATION: 30,
            EMERGENCY: 15,
          };
          const duration = appointmentTypes[selectedType] || 30;
          const slots = await medicalDB.getSuggestedTimeSlots(
            selectedDate,
            duration
          );
          setAvailableTimeSlots(slots.slice(0, 8)); // Show up to 8 suggestions
        } catch (error) {
          console.error("Error loading available time slots:", error);
          setAvailableTimeSlots([]);
        }
      } else {
        setAvailableTimeSlots([]);
      }
    };

    loadAvailableSlots();
  }, [selectedDate, selectedType]);

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);
      const patientsData = await medicalDB.getAllPatients();
      setPatients(patientsData);
    } catch (error) {
      console.error("Error loading patients:", error);
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const appointmentData = await medicalDB.getAppointment(id);
      if (appointmentData) {
        setAppointment(appointmentData);
        // Populate form with existing data
        setValue("patientId", appointmentData.patientId);
        setValue("date", appointmentData.date.split("T")[0]); // Format date for input
        setValue("startTime", appointmentData.startTime);
        setValue("endTime", appointmentData.endTime);
        setValue("type", appointmentData.type);
        setValue("notes", appointmentData.notes || "");
      } else {
        navigate("/appointments");
      }
    } catch (error) {
      console.error("Error loading appointment:", error);
      navigate("/appointments");
    } finally {
      setLoading(false);
    }
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(":").map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);

    const endDate = new Date(startDate.getTime() + durationMinutes * 60000);

    return endDate.toTimeString().slice(0, 5); // Format as HH:MM
  };

  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 8; // 8 AM
    const endHour = 18; // 6 PM

    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        slots.push({
          value: timeString,
          label: formatTimeForDisplay(timeString),
        });
      }
    }

    return slots;
  };

  const formatTimeForDisplay = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getQuickDateOptions = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);

    const options = [
      {
        label: "Today",
        value: today.toISOString().split("T")[0],
      },
      {
        label: "Tomorrow",
        value: tomorrow.toISOString().split("T")[0],
      },
    ];

    // Add day after tomorrow with day name
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayAfterTomorrowName = dayNames[dayAfterTomorrow.getDay()];
    options.push({
      label: `This ${dayAfterTomorrowName}`,
      value: dayAfterTomorrow.toISOString().split("T")[0],
    });

    return options;
  };

  const patientOptions = patients.map((patient) => ({
    value: patient.id,
    label: `${patient.name} (Age: ${patient.age}, ${patient.gender})`,
  }));

  const appointmentTypes = {
    ROUTINE: { name: "Routine Checkup", duration: 30 },
    FOLLOW_UP: { name: "Follow-up", duration: 20 },
    NEW_PATIENT: { name: "New Patient", duration: 45 },
    CONSULTATION: { name: "Consultation", duration: 30 },
    EMERGENCY: { name: "Emergency", duration: 15 },
  };

  const appointmentTypeOptions = Object.entries(appointmentTypes).map(
    ([key, value]) => ({
      value: key,
      label: `${value.name} (${value.duration} min)`,
    })
  );

  const timeSlotOptions = generateTimeSlots();

  const onSubmit = async (data) => {
    try {
      const appointmentData = {
        patientId: data.patientId,
        date: data.date,
        startTime: data.startTime,
        endTime: data.endTime,
        type: data.type,
        notes: data.notes,
      };

      if (isEditing) {
        await medicalDB.updateAppointment(id, appointmentData);
      } else {
        await medicalDB.createAppointment(appointmentData);
      }

      // Dispatch event to refresh sidebar schedule
      window.dispatchEvent(new CustomEvent("appointmentChanged"));

      navigate("/appointments");
    } catch (error) {
      console.error("Error saving appointment:", error);
      alert("Failed to save appointment. Please try again.");
    }
  };

  if (loading || loadingPatients) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600 dark:text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/appointments")}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? "Edit Appointment" : "New Appointment"}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isEditing
              ? "Update appointment details"
              : "Schedule a new patient appointment"}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
            <User className="h-5 w-5 text-medical-blue" />
            <span>Patient Information</span>
          </h2>

          <div className="space-y-4">
            <div>
              <label className="form-label">Patient *</label>
              <Controller
                name="patientId"
                control={control}
                rules={{ required: "Please select a patient" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={patientOptions}
                    value={patientOptions.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value || "")
                    }
                    placeholder="Search and select a patient..."
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                )}
              />
              {errors.patientId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                  <AlertCircle size={16} />
                  <span>{errors.patientId.message}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-medical-blue" />
            <span>Appointment Details</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Date *</label>
              <input
                type="date"
                min={getMinDate()}
                {...register("date", { required: "Please select a date" })}
                className="form-input"
              />

              {/* Quick Date Selection Chips */}
              <div className="mt-2 flex flex-wrap gap-2">
                {getQuickDateOptions().map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setValue("date", option.value)}
                    className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                      selectedDate === option.value
                        ? "bg-medical-blue text-white border-medical-blue"
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {errors.date && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                  <AlertCircle size={16} />
                  <span>{errors.date.message}</span>
                </p>
              )}
            </div>

            <div>
              <label className="form-label">Appointment Type *</label>
              <Controller
                name="type"
                control={control}
                rules={{ required: "Please select appointment type" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={appointmentTypeOptions}
                    value={appointmentTypeOptions.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value || "")
                    }
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                )}
              />
              {errors.type && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                  <AlertCircle size={16} />
                  <span>{errors.type.message}</span>
                </p>
              )}
            </div>

            <div>
              <label className="form-label">Start Time *</label>
              <Controller
                name="startTime"
                control={control}
                rules={{ required: "Please select start time" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={timeSlotOptions}
                    value={timeSlotOptions.find(
                      (option) => option.value === field.value
                    )}
                    onChange={(selectedOption) =>
                      field.onChange(selectedOption?.value || "")
                    }
                    placeholder="Select time..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                )}
              />

              {/* Available Time Suggestion Chips */}
              {availableTimeSlots.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Available times:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {availableTimeSlots.map((slot, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => {
                          setValue("startTime", slot.startTime);
                          setValue("endTime", slot.endTime);
                        }}
                        className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                          selectedStartTime === slot.startTime
                            ? "bg-green-500 text-white border-green-500"
                            : "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:border-green-600 dark:hover:bg-green-900/40"
                        }`}
                      >
                        {slot.startTime}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {errors.startTime && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center space-x-1">
                  <AlertCircle size={16} />
                  <span>{errors.startTime.message}</span>
                </p>
              )}
            </div>

            <div>
              <label className="form-label">End Time</label>
              <input
                type="time"
                {...register("endTime")}
                className="form-input bg-gray-50 dark:bg-gray-700"
                readOnly
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Automatically calculated based on appointment type
              </p>
            </div>
          </div>
        </div>

        {/* Conflict Warning */}
        {showConflictWarning && conflicts.length > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
                  Time Conflict Detected
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>This appointment time conflicts with:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    {conflicts.map((conflict) => (
                      <li key={conflict.id}>
                        {conflict.patient?.name} - {conflict.startTime} to{" "}
                        {conflict.endTime}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Suggested Time Slots */}
        {suggestedSlots.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-3">
              Suggested Available Times
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {suggestedSlots.map((slot, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    setValue("startTime", slot.startTime);
                    setValue("endTime", slot.endTime);
                  }}
                  className="text-sm bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/50 px-3 py-2 rounded-lg transition-colors text-blue-700 dark:text-blue-300"
                >
                  {slot.startTime} - {slot.endTime}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center space-x-2">
            <FileText className="h-5 w-5 text-medical-blue" />
            <span>Additional Information</span>
          </h2>

          <div>
            <label className="form-label">Notes</label>
            <textarea
              {...register("notes")}
              rows={4}
              placeholder="Add any special instructions or notes for this appointment..."
              className="form-input"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/appointments")}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary flex items-center space-x-2"
          >
            <Save size={20} />
            <span>{isSubmitting ? "Saving..." : "Save Appointment"}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AppointmentForm;
